import { useEffect, useState } from 'react';
import { NAV_ITEMS } from '@/data/site.ts';
import { href } from '@/lib/paths.ts';
import { Logo } from './Logo.tsx';

/**
 * Fixed site header. `onLight` is for pages whose hero has a light
 * background instead of a dark image, and so need dark nav text from the
 * start rather than after the scroll threshold.
 */
export function Nav({
  currentPath,
  onLight = false,
}: {
  currentPath: string;
  onLight?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the fullscreen menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Escape closes the menu.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const classes = [
    'nav',
    scrolled ? 'nav--scrolled' : '',
    onLight ? 'nav--on-light' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {menuOpen && <div className="nav__backdrop" aria-hidden="true" />}
      <nav className={classes} aria-label="Primary">
        <div className="nav__inner">
          <a href={href('/')} className="nav__logo" aria-label="Home">
            <Logo />
          </a>

          <div
            id="nav-menu"
            className={`nav__links ${menuOpen ? 'nav__links--open' : ''}`.trim()}
            onClick={(e) => {
              // Clicking the overlay background (not a link) closes the menu.
              if (e.target === e.currentTarget) setMenuOpen(false);
            }}
          >
            {NAV_ITEMS.map((item) => {
              const active = item.href === currentPath;
              return (
                <a
                  key={item.href}
                  href={href(item.href)}
                  className={`nav__link ${active ? 'nav__link--active' : ''}`.trim()}
                  {...(active ? { 'aria-current': 'page' as const } : {})}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </a>
              );
            })}
            <div className="nav__drawer-actions">
              <a
                href="https://learnpsl.ai/login"
                className="nav__login nav__login--drawer"
                onClick={() => setMenuOpen(false)}
              >
                <span>Login</span>
              </a>
              <a
                href={href('/contact')}
                className="nav__cta nav__cta--drawer"
                onClick={() => setMenuOpen(false)}
              >
                <span>Get Started</span>
              </a>
            </div>
          </div>

          <div className="nav__right">
            <a href="https://learnpsl.ai/login" className="nav__login">
              <span>Login</span>
            </a>
            <a href={href('/contact')} className="nav__cta">
              <span>Get Started</span>
            </a>
            <button
              type="button"
              className={`nav__toggle ${menuOpen ? 'nav__toggle--active' : ''}`.trim()}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls="nav-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
